import { useMemo, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

import { useStoreDataTableSelectedFeature } from '@/core/stores/states/data-table-state';
import { useStoreUIActiveFocusItem, useStoreUIActiveAppBarTab } from '@/core/stores/states/ui-state';
import { useStoreAppShellContainer } from '@/core/stores/states/app-state';

import { Modal } from '@/ui/modal/modal';
import { List } from '@/ui/list/list';
import { Box } from '@/ui/layout';
import { Typography } from '@/ui/typography/typography';
import { BrowserNotSupportedIcon } from '@/ui/icons';

import { useUIController } from '@/core/controllers/use-controllers';
import { getSxClasses } from './details-style';
import { FeatureInfoTable } from './feature-info-table';
import type { TypeFieldEntry } from '@/api/types/map-schema-types';
import { logger } from '@/core/utils/logger';
import { TABS, LIGHTBOX_SELECTORS } from '@/core/utils/constant';

/**
 * Creates the lightweight feature detail modal component.
 *
 * @returns The feature detail modal component
 */
export default function FeatureDetailModal(): JSX.Element {
  // Log
  logger.logTraceRender('components/details/feature-detail-modal');

  const { t } = useTranslation();

  const theme = useTheme();
  const sxClasses = getSxClasses(theme);

  // get store function
  const uiController = useUIController();
  const activeModalId = useStoreUIActiveFocusItem().activeElementId;
  const feature = useStoreDataTableSelectedFeature()!;
  const shellContainer = useStoreAppShellContainer();

  // Determine which container (appBar or footerBar) the modal is rendered in
  const activeAppBarTab = useStoreUIActiveAppBarTab();
  const containerType = activeAppBarTab.tabId === TABS.DATA_TABLE && activeAppBarTab.isOpen ? 'appBar' : 'footerBar';

  // State
  const [nameFieldValue, setNameFieldValue] = useState<string>('');

  /**
   * Builds the features list to display in the table.
   */
  const memoFeatureInfoList: TypeFieldEntry[] = useMemo(() => {
    // Log
    logger.logTraceUseMemo('DETAILS PANEL - Feature Detail Modal - featureInfoList');

    // Extract value of field info nameField for item symbol description
    const key = feature.nameField;
    setNameFieldValue(key && feature.fieldInfo?.[key]?.value ? (feature.fieldInfo[key].value as string) : '');

    const featureInfo = Object.keys(feature?.fieldInfo ?? {}).map((fieldName) => {
      return {
        fieldKey: feature.fieldInfo[fieldName]!.fieldKey,
        value: feature.fieldInfo[fieldName]!.value,
        dataType: feature.fieldInfo[fieldName]!.dataType,
        alias: feature.fieldInfo[fieldName]!.alias ? feature.fieldInfo[fieldName]!.alias : fieldName,
      };
    });

    // Remove last item who is the geoviewID
    featureInfo.pop();

    return featureInfo;
  }, [feature]);

  return (
    <Modal
      modalId="featureDetailDataTable"
      open={activeModalId === 'featureDetailDataTable' && !!feature}
      onClose={() => {
        // Don't close the modal if the LightBox is open
        // The LightBox will handle its own close and focus restoration
        // The lightbox is a single page-wide overlay (yarl portal); a global check is intentional.
        // eslint-disable-next-line no-restricted-syntax
        const lightboxOpen = document.querySelector(LIGHTBOX_SELECTORS.ROOT);
        if (!lightboxOpen) {
          uiController.disableFocusTrap();
        }
      }}
      title={t('details.featureDetailModalTitle')}
      container={shellContainer}
      width="90vw"
      contentModal={
        <>
          <Box sx={sxClasses.featureDetailHeader}>
            {feature.featureIcon ? (
              <Box component="img" alt="" src={feature.featureIcon} className="layer-icon" />
            ) : (
              <Box component="div" aria-label={feature?.nameField ?? ''} className="layer-icon">
                <BrowserNotSupportedIcon />
              </Box>
            )}
            <Typography sx={sxClasses.inlineBlock} component="div">
              {nameFieldValue}
            </Typography>
          </Box>
          <List sx={sxClasses.featureDetailListContainer}>
            <FeatureInfoTable layerPath={feature.layerPath} featureInfoList={memoFeatureInfoList} containerType={containerType} />
          </List>
        </>
      }
    />
  );
}
